<template>
  <div class="ps_container">
    <div class="ps_header">
      <span class="heading-2">{{ $t("settings.profile.title") }}</span>
      <PrimaryButton :text="$t('settings.profile.save')" :loading="buttonLoading" @onClick="saveChanges" />
    </div>
    <v-divider class="ps_divider"></v-divider>
    <v-card outlined>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <UploadHeader />
      <div class="ps_details">
        <span class="heading-4">{{ $t("settings.profile.title") }}</span>
        <v-row class="ps_row">
          <v-col cols="12" md="4" class="ps_input-group">
            <span>{{ $t("general.inputs.firstName") }}</span
            ><BaseInput v-model="firstName" :errors="firstNameErrors" fieldName="firstName" @blur="$v.email.$touch()"
          /></v-col>
          <v-col cols="12" md="4" class="ps_input-group">
            <span>{{ $t("general.inputs.lastName") }}</span
            ><BaseInput v-model="lastName" :errors="lastNameErrors" fieldName="lastName" @blur="$v.email.$touch()" />
          </v-col>
          <v-col cols="12" md="4" class="ps_input-group">
            <span>{{ $t("general.inputs.dateOfBirth") }}</span>
            <DatePicker
              v-model="dateOfBirth"
              :dense="true"
              :height="47"
              :errors="dateOfBirthErrors"
              :maxDate="new Date().toISOString().substr(0, 10)"
              @blur="$v.dateOfBirth.$touch()"
            />
          </v-col>
        </v-row>
        <v-row class="ps_row">
          <v-col cols="12" md="4" class="ps_input-group">
            <span>{{ $t("general.inputs.state") }}</span>
            <StateSelect v-model="state"
          /></v-col>
          <v-col cols="12" md="4" class="ps_input-group">
            <span>Mobile phone number</span>
            <PhoneInput v-model="phoneNumber" fieldName="phoneNumber" />
          </v-col>
          <v-col cols="12" md="4" class="ps_input-group">
            <span>Home phone number</span>
            <PhoneInput v-model="homePhoneNumber" fieldName="homePhoneNumber" />
          </v-col>
          <v-col cols="12" md="4" class="ps_input-group">
            <span>{{ $t("general.inputs.address") }}</span>
            <BaseInput v-model="address" fieldName="address" />
          </v-col>
        </v-row>
      </div>
      <div class="ps_details">
        <span class="heading-4">{{ $t("settings.profile.accountDetails") }}</span>
        <v-row class="ps_row">
          <v-col cols="12" md="4" class="ps_input-group">
            <span>{{ $t("general.inputs.email") }}</span
            ><BaseInput
              v-model="email"
              :errors="emailErrors"
              fieldName="email"
              :disabled="true"
              @blur="$v.email.$touch()"
          /></v-col>
          <v-col cols="4" class="ps_input-group">
            <span>{{ $t("settings.language") }}</span>
            <Select v-model="locale" :items="languages" :label="$t('settings.selectLanguage')" />
          </v-col>
        </v-row>
      </div>
      <ChangePassword />
      <div class="ps_details">
        <span class="heading-4">{{ $t("settings.profile.generalMedicalInformation") }}</span>
        <v-row class="ps_row">
          <v-col cols="12" md="4" class="ps_input-group">
            <span>{{ $t("general.inputs.height") }}</span
            ><BaseInput
              v-model="height"
              :placeholder="$t('general.inputs.height')"
              fieldName="height"
              maskPattern="####"
          /></v-col>
          <v-col cols="12" md="4" class="ps_input-group">
            <span>{{ $t("general.inputs.weight") }}</span
            ><BaseInput
              v-model="weight"
              :placeholder="$t('general.inputs.weight')"
              fieldName="weight"
              maskPattern="####"
            />
          </v-col>
          <v-col cols="12" md="4" class="ps_input-group">
            <span>{{ $t("settings.profile.smokingStatus") }}</span>
            <Switcher v-model="isSmoking" :items="$t('general.smokingStatus')" />
          </v-col>
        </v-row>
      </div>
      <div class="ps_details">
        <span class="heading-4 ps_margin-bottom">{{ $t("settings.profile.pastMedicalHistory") }}</span>
        <PatientHistoryTable
          id="PMH"
          place="visitNotes"
          :items="pmh"
          @change="onPMHChange"
          tableClassName="ph-table_visit-notes_table"
        />
      </div>
      <div class="ps_details">
        <span class="heading-4 ps_margin-bottom">{{ $t("settings.profile.allergies") }}</span>
        <Combobox placeholder="Enter allergies" :items="allergies" @change="onAllergiesChange" />
      </div>
      <div class="ps_details">
        <span class="heading-4 ps_margin-bottom">{{ $t("settings.profile.medications") }}</span>
        <MedicationTable
          place="visitNotes"
          :items="medications"
          tableClassName="medications-table_visit-notes_table"
          @change="onMedicalInfoChange"
        />
      </div>
    </v-card>
  </div>
</template>
<script>
import { email, required } from "vuelidate/lib/validators";
import { mapActions, mapGetters, mapMutations } from "vuex";

import MedicationTable from "@/components/Medications/Table";
import PatientHistoryTable from "@/components/PatientHistoryTable/Table";
import ChangePassword from "@/components/settings/changePassword";
import UploadHeader from "@/components/settings/uploadHeader";
import DatePicker from "@/components/shared/DatePicker";
import PhoneInput from "@/components/shared/PhoneInput";
import BaseInput from "@/components/uikit/BaseInput";
import Combobox from "@/components/uikit/Combobox";
import PrimaryButton from "@/components/uikit/PrimaryButton";
import Select from "@/components/uikit/Select";
import Switcher from "@/components/uikit/Switcher";
import { snackBarEventBus, snackBarEventName } from "@/eventBuses/snackBar.eventBus";
import { LANGUAGES } from "@/utils/languages";

import StateSelect from "../shared/StateSelect";

export default {
  name: "PatientSettings",
  components: {
    PrimaryButton,
    UploadHeader,
    BaseInput,
    ChangePassword,
    DatePicker,
    MedicationTable,
    PatientHistoryTable,
    Combobox,
    StateSelect,
    Switcher,
    PhoneInput,
    Select,
  },
  validations: {
    email: { required, email },
    firstName: { required },
    lastName: { required },
    dateOfBirth: { required },
    homePhoneNumber: { required },
  },
  data: () => ({
    loading: false,
    buttonLoading: false,
    firstNameModel: "",
    lastNameModel: "",
    emailModel: "",
    dateOfBirthModel: null,
    stateModel: "",
    homePhoneNumberModel: "",
    phoneNumberModel: "",
    addressModel: "",
    heightModel: "",
    weightModel: "",
    isSmokingModel: "false",
    localeModel: "",
    languages: LANGUAGES,
  }),
  computed: {
    ...mapGetters("profileSettings", ["patientSettings", "avatar", "generalInformation", "medicalInformation"]),
    emailErrors() {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      !this.$v.email.email && errors.push(this.$t("validation.email"));
      !this.$v.email.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.email") }));
      return errors;
    },
    firstNameErrors() {
      const errors = [];
      if (!this.$v.firstName.$dirty) return errors;
      !this.$v.firstName.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.firstName") }));
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.lastName.$dirty) return errors;
      !this.$v.lastName.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.lastName") }));
      return errors;
    },
    dateOfBirthErrors() {
      const errors = [];
      if (!this.$v.dateOfBirth.$dirty) return errors;
      !this.$v.dateOfBirth.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.dateOfBirth") }));
      return errors;
    },
    homePhoneNumberErrors() {
      const errors = [];
      if (!this.$v.homePhoneNumber.$dirty) return errors;
      !this.$v.homePhoneNumber.required && errors.push(this.$t("validation.required", { field: "Home phone number" }));
      return errors;
    },
    medications() {
      if (!this.medicalInformation || !this.medicalInformation.medications) {
        return [];
      }
      return this.medicalInformation.medications.map((el) => ({
        ...el,
        startDate: new Date(el.startDate),
      }));
    },
    allergies() {
      if (!this.medicalInformation || !this.medicalInformation.allergies) {
        return [];
      }
      return this.medicalInformation.allergies;
    },
    pmh() {
      if (!this.medicalInformation || !this.medicalInformation.pmh) {
        return [];
      }
      return this.medicalInformation.pmh;
    },
    firstName: {
      get() {
        return this.firstNameModel || this.patientSettings.firstName;
      },
      set(v) {
        this.firstNameModel = v;
      },
    },
    lastName: {
      get() {
        return this.lastNameModel || this.patientSettings.lastName;
      },
      set(v) {
        this.lastNameModel = v;
      },
    },
    email: {
      get() {
        return this.emailModel || this.patientSettings.email;
      },
      set(v) {
        this.emailModel = v;
      },
    },
    dateOfBirth: {
      get() {
        return this.dateOfBirthModel || this.patientSettings.birthDate
          ? new Date(this.patientSettings.birthDate)
          : new Date();
      },
      set(v) {
        this.dateOfBirthModel = v;
      },
    },
    state: {
      get() {
        return this.stateModel || this.patientSettings.state;
      },
      set(v) {
        this.stateModel = v;
      },
    },
    address: {
      get() {
        return this.addressModel || this.patientSettings.address;
      },
      set(v) {
        this.addressModel = v;
      },
    },
    phoneNumber: {
      get() {
        return this.phoneNumberModel || this.patientSettings.phoneNumber;
      },
      set(v) {
        this.phoneNumberModel = v;
      },
    },
    homePhoneNumber: {
      get() {
        return this.homePhoneNumberModel || this.patientSettings.homePhoneNumber;
      },
      set(v) {
        this.homePhoneNumberModel = v;
      },
    },
    weight: {
      get() {
        return this.weightModel || this.generalInformation.weight;
      },
      set(v) {
        this.weightModel = v;
      },
    },
    height: {
      get() {
        return this.heightModel || this.generalInformation.height;
      },
      set(v) {
        this.heightModel = v;
      },
    },
    isSmoking: {
      get() {
        return this.isSmokingModel || this.generalInformation.isSmoking;
      },
      set(v) {
        this.isSmokingModel = v;
      },
    },
    locale: {
      get() {
        return this.localeModel || this.patientSettings.locale;
      },
      set(v) {
        this.localeModel = v;
      },
    },
  },
  methods: {
    ...mapActions("profileSettings", ["savePatientChanges"]),
    ...mapMutations("profileSettings", ["setMedicalInformation"]),
    onAllergiesChange(value) {
      this.setMedicalInformation({ value, fieldName: "allergies" });
    },
    onMedicalInfoChange(value) {
      this.setMedicalInformation({ value, fieldName: "medications" });
    },
    onPMHChange(value) {
      this.setMedicalInformation({ value, fieldName: "pmh" });
    },
    async saveChanges() {
      this.$gtag.event("click", { method: "Save Patient Settings" });
      this.buttonLoading = true;
      try {
        await this.savePatientChanges({
          locale: this.locale,
          profile: {
            name: { firstName: this.firstName, lastName: this.lastName },
            address: { address: this.address, state: this.state },
            birthDate: this.dateOfBirth.toISOString(),
            homePhoneNumber: this.homePhoneNumber,
            phoneNumber: this.phoneNumber,
          },
          generalInformation: {
            height: this.height,
            weight: this.weight,
            isSmoking: this.isSmoking,
          },
        });
        this.$root.$i18n.locale = this.locale;
        snackBarEventBus.$emit(snackBarEventName, {
          message: this.$t("settings.success"),
          type: "success",
        });
      } catch (err) {
        snackBarEventBus.$emit(snackBarEventName, {
          message: (err && err.response && err.response.data && err.response.data.message) || err,
          type: "error",
        });
      } finally {
        this.buttonLoading = false;
      }
    },
  },
  mounted() {
    this.loading = true;
  },
  updated() {
    if (this.patientSettings.firstName) this.loading = false;
  },
};
</script>
